import {
  TextField,
  DateField,
  SelectField,
  SelectInput,
  Datagrid,
  useRecordContext,
  SimpleShowLayout,
  Button,
  SimpleForm,
  required,
  useNotify,
  useRefresh,
} from "react-admin";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { candidateRequests } from "api/candidateApi";
import { settings } from "app";
import RankingButtons from "./RankingButtons";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useState } from "react";
import CustomDateTimeInput from "server/components/Fields/CustomDateTimeInput";
import dataProvider from "server/dataProvider";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { RankingStatusChoices, CandidateCampaignRankingTypeChoices } from "utils/choices";
import { RankingStatus, RankingType } from "utils/constants";
import { useNotifyError } from "utils/useNotifyError";
import { SectionTitle } from "features/SectionTitle";


const RankingsList = ({ isFinished, rankingTypes = [RankingType.State] }) => {
  const typeChoices = CandidateCampaignRankingTypeChoices.filter(choice => rankingTypes.includes(choice.id));
  const [defaultType] = rankingTypes;
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const [isEditMode, setIsEditMode] = useState(false);
  if (!record) {
    return null;
  }

  const campaignId = record?.id;

  const indexedRankings =
    record.rankings?.map((ranking, i) => ({
      ...ranking,
      index: `#${i + 1}`,
    })) || [];

  const hasPaidDraftGenerated = indexedRankings.some(
    r => r.type === RankingType.Paid && r.status >= RankingStatus.Draft,
  );

  const handleSubmit = async formData => {
    const body = {
      rankingDate: formData.rankingDate,
      enrollmentStartDate: formData.enrollmentStartDate,
      enrollmentEndDate: formData.enrollmentEndDate,
      type: formData.type ?? defaultType,
    };
    try {
      await dataProvider.customRequest(candidateRequests.createCampaignRanking(campaignId), body, "POST");
      notify("Успешно добавихте класиране", { type: "success" });
      refresh();
      setIsEditMode(false);
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <SimpleShowLayout>
      <SectionTitle
        title="Класирания"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
        {!isEditMode ? (
          <>
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={{ margin: 2, ...settings.style.secondaryDatagrid }}
              data={indexedRankings}
            >
              <TextField
                label="№"
                source="index"
                sortable={false}
              />
              <DateField
                source="rankingDate"
                label="Дата на класиране"
                showTime
                locales="bg-BG"
                sortable={false}
              />
              <DateField
                source="enrollmentStartDate"
                label="Начало на записване"
                showTime
                locales="bg-BG"
                sortable={false}
              />
              <DateField
                source="enrollmentEndDate"
                label="Край на записване"
                showTime
                locales="bg-BG"
                sortable={false}
              />
              <SelectField
                source="status"
                label="Статус"
                choices={RankingStatusChoices}
                sortable={false}
              />
              <SelectField
                source="type"
                label="Тип"
                choices={CandidateCampaignRankingTypeChoices}
                sortable={false}
              />
              <RankingButtons
                isFinished={isFinished}
                hasPaidDraftGenerated={hasPaidDraftGenerated}
              />
            </Datagrid>
            {!isFinished && campaignId && (
              <SecuredComponent permission={permissions.canUpdateCampaigns}>
                <Button
                  startIcon={<AddCircleIcon />}
                  variant="contained"
                  disabled={""}
                  size="medium"
                  sx={{ ml: 2 }}
                  label="Добави"
                  onClick={() => setIsEditMode(true)}
                />
              </SecuredComponent>
            )}
          </>
        ) : (
          <SimpleForm
            toolbar={false}
            onSubmit={handleSubmit}
          >
            <Grid
              container
              spacing={2}
              mb={2}
            >
              <Grid
                item
                xs={12}
                md={4}
              >
                <CustomDateTimeInput
                  source="rankingDate"
                  label="Дата на класиране"
                  validate={required()}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <CustomDateTimeInput
                  source="enrollmentStartDate"
                  label="Начало на записване"
                  validate={required()}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <CustomDateTimeInput
                  source="enrollmentEndDate"
                  label="Край на записване"
                  validate={required()}
                />
              </Grid>
              {typeChoices.length > 1 && (
                <Grid
                  item
                  xs={12}
                  md={4}
                >
                  <SelectInput
                    source="type"
                    label="Тип класиране"
                    choices={typeChoices}
                    defaultValue={defaultType}
                    validate={required()}
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
            <Box sx={{ textAlign: "left" }}>
              <Button
                label="Откажи"
                onClick={() => setIsEditMode(false)}
                variant="outlined"
                color="error"
                size="medium"
                startIcon={<CloseIcon />}
                sx={{ mr: 10 }}
              />

              <Button
                label="Запази"
                type="submit"
                color="primary"
                variant="contained"
                size="medium"
                startIcon={<SaveIcon />}
              />
            </Box>
          </SimpleForm>
        )}
      </Box>
    </SimpleShowLayout>
  );
};

export default RankingsList;
